import React from 'react'

import { Avatar, AvatarProps } from '@mui/material'

import GameIcon from '../../../game/components/GameIcon/GameIcon'
import { NewAdopterGame, gameInitial } from '../helpers/newAdopterGames'

interface NewAdopterGameIconProps extends Omit<AvatarProps, 'children' | 'src' | 'alt'> {
  game: Pick<NewAdopterGame, 'name' | 'iconUrl' | 'appType' | 'platforms'>
  // Edge length in px. Only sizes the platform badge: the box itself is sized by the caller (or by the
  // `AvatarGroup` this sits in), the same as it was for the plain avatar this replaces.
  size: number
  // Set where the game's name is printed right beside the icon, so it isn't announced twice.
  decorative?: boolean
  // Defaults to on. Turned off where the icon is too small for the badge plate to stay legible.
  showPlatformBadge?: boolean
}

/**
 * A New Adopters game face: the game's icon with its platform (mobile / PC-console) badge, in an
 * `Avatar` shell so it keeps taking the sizing, ring and overlap `AvatarGroup` and the callers give
 * their avatars. A game with no icon on record keeps the letter avatar.
 *
 * The badge is `GameIcon`'s own, and sizes itself off the `--game-icon-size` this sets.
 */
export const NewAdopterGameIcon: React.FC<NewAdopterGameIconProps> = ({ game, size, decorative, showPlatformBadge = true, sx, ...avatarProps }) => {
  if (!game.iconUrl) {
    return (
      <Avatar variant="rounded" alt={decorative ? '' : game.name} sx={sx} {...avatarProps}>
        {gameInitial(game.name)}
      </Avatar>
    )
  }

  return (
    <Avatar variant="rounded" aria-hidden={decorative || undefined} sx={[{ bgcolor: 'transparent' }, ...(Array.isArray(sx) ? sx : [sx])]} {...avatarProps}>
      <GameIcon
        gameName={decorative ? '' : game.name}
        src={game.iconUrl}
        size="xtiny"
        appType={showPlatformBadge ? game.appType : undefined}
        gamePlatforms={showPlatformBadge ? game.platforms : undefined}
        rootStyle={
          {
            width: '100%',
            height: '100%',
            borderRadius: 'inherit',
            boxShadow: 'none',
            '--game-icon-size': `${size}px`,
          } as React.CSSProperties
        }
      />
    </Avatar>
  )
}
